"use client";

import { SignInButton, SignUpButton } from "@clerk/nextjs";
import { ArrowLeftRight } from "lucide-react";
import { SignedOut } from "modules/auth/components/ClientAuthGate";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { useVisitorCtaEvents } from "./useVisitorCtaEvents";
import {
  FUNNEL_CTA,
  FUNNEL_EYEBROW,
  FUNNEL_HEADLINE,
  FUNNEL_PILLARS,
  FunnelContext,
  buildFunnelLede,
} from "./VisitorFunnelCopy";

export type { FunnelContext, FunnelDirection } from "./VisitorFunnelCopy";

interface VisitorFunnelCardProps {
  context: FunnelContext;
}

/**
 * Anonymous-visitor conversion card shown alongside any trade-item facet
 * sidebar. Anonymous viewers only, and not before Clerk resolves, so signed-in
 * users never see the upsell — not even as a flash.
 *
 * All copy lives in VisitorFunnelCopy.ts — this component just renders.
 */
export function VisitorFunnelCard({ context }: VisitorFunnelCardProps) {
  return (
    <SignedOut waitForClerk>
      <Card context={context} />
    </SignedOut>
  );
}

function Card({ context }: VisitorFunnelCardProps) {
  // The aside is display:none below md, so the card is in the DOM but not
  // seen — only count a view when the sidebar is actually shown.
  const isSidebarVisible = useMediaQuery("(min-width: 768px)");
  const onCtaClick = useVisitorCtaEvents(context, "card", isSidebarVisible);

  return (
    <div className="card shadow-xs border-base-content/20 border">
      <div className="card-body gap-3 p-4">
        <div>
          <p className="text-primary text-xs font-bold uppercase tracking-[0.14em]">
            {FUNNEL_EYEBROW}
          </p>
          <h3 className="font-display mt-1.5 text-lg font-bold leading-tight tracking-tight">
            {FUNNEL_HEADLINE.before}
            <em className="text-primary not-italic">
              {FUNNEL_HEADLINE.emphasized}
            </em>
            {FUNNEL_HEADLINE.after}
          </h3>
        </div>

        <p className="text-text-secondary text-sm leading-snug">
          {buildFunnelLede(context)}
        </p>

        <ul className="border-base-content/10 divide-base-content/10 flex flex-col divide-y border-y">
          {FUNNEL_PILLARS.map(({ icon, title, description }) => (
            <li key={title} className="flex items-start gap-2.5 py-2.5">
              <span className="border-base-content/15 bg-base-200 text-primary flex size-7 shrink-0 items-center justify-center rounded-md border">
                {icon}
              </span>
              <div className="leading-snug">
                <div className="text-sm font-semibold">{title}</div>
                <div className="text-text-secondary text-xs">{description}</div>
              </div>
            </li>
          ))}
        </ul>

        <SignUpButton mode="modal">
          <button
            className="btn btn-primary btn-sm gap-1.5"
            onClick={onCtaClick}
          >
            <ArrowLeftRight size={14} />
            {FUNNEL_CTA.primary}
          </button>
        </SignUpButton>

        <p className="text-text-tertiary text-center text-xs">
          {FUNNEL_CTA.signInPrompt}
          <SignInButton mode="modal">
            <button className="text-text-secondary hover:text-base-content underline underline-offset-2">
              {FUNNEL_CTA.signInLink}
            </button>
          </SignInButton>
        </p>
      </div>
    </div>
  );
}
